import React from "react";
import { motion } from "framer-motion";
import {
    Browsers,
    Camera,
    FilmSlate,
    PenNib,
    ArrowUpRight,
} from "@phosphor-icons/react";

const SERVICES = [
    {
        id: "web",
        title: "Sites web sur mesure",
        kicker: "01 — Web",
        desc: "Conception, design et développement de sites rapides, élégants et pensés pour convertir.",
        icon: Browsers,
        accent: "yellow",
        size: "lg:col-span-7 lg:row-span-2",
        image:
            "https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=1200&q=80",
    },
    {
        id: "photo",
        title: "Photographie",
        kicker: "02 — Photo",
        desc: "Séances corporatives, produits, événements & lifestyle.",
        icon: Camera,
        accent: "green",
        size: "lg:col-span-5",
    },
    {
        id: "video",
        title: "Production vidéo",
        kicker: "03 — Vidéo",
        desc: "Capsules, publicités, contenu social — du concept au montage.",
        icon: FilmSlate,
        accent: "yellow",
        size: "lg:col-span-5",
    },
    {
        id: "logo",
        title: "Logo & identité",
        kicker: "04 — Branding",
        desc: "Identités de marque cohérentes et mémorables, du logo à la charte complète.",
        icon: PenNib,
        accent: "green",
        size: "lg:col-span-12",
    },
];

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16"
        >
            {/* Header */}
            <div className="grid md:grid-cols-12 gap-8 mb-14 md:mb-20">
                <div className="md:col-span-4">
                    <span className="label-eyebrow text-yellow">— Services</span>
                </div>
                <div className="md:col-span-8">
                    <h2 className="section-heading text-cream" data-testid="services-heading">
                        Une équipe créative,
                        <br />
                        <span className="text-yellow italic">quatre disciplines</span>{" "}
                        pour propulser votre marque.
                    </h2>
                    <p className="mt-6 max-w-2xl text-cream/75 text-lg">
                        Du concept à la production finale, nous prenons en charge
                        chaque détail visuel pour que votre entreprise se démarque
                        avec confiance et cohérence.
                    </p>
                </div>
            </div>

            {/* Bento grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-6">
                {SERVICES.map((s, i) => (
                    <ServiceCard key={s.id} service={s} index={i} />
                ))}
            </div>
        </section>
    );
}

function ServiceCard({ service, index }) {
    const { icon: Icon, accent, size, image } = service;
    const accentBg = accent === "yellow" ? "bg-yellow" : "bg-green";
    const accentText = accent === "yellow" ? "text-[#272727]" : "text-cream";

    return (
        <motion.article
            initial={{ y: 60, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{
                y: -8,
                rotateX: 2,
                rotateY: -1.5,
                transition: { duration: 0.4 },
            }}
            style={{ transformStyle: "preserve-3d", perspective: "1000px" }}
            className={`lift relative overflow-hidden border border-scd bg-elev rounded-sm p-7 md:p-9 min-h-[240px] flex flex-col justify-between ${size || ""}`}
            data-testid={`service-card-${service.id}`}
        >
            {image && (
                <div
                    className="absolute inset-0 opacity-25 group-hover:opacity-40 transition-opacity"
                    style={{
                        backgroundImage: `linear-gradient(180deg, rgba(250,247,241,0.4) 0%, rgba(250,247,241,0.92) 100%), url(${image})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
            )}
            <div className="relative z-10 flex items-start justify-between">
                <div
                    className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${accentBg} ${accentText}`}
                >
                    <Icon size={22} weight="bold" />
                </div>
                <span className="label-eyebrow text-cream/60">{service.kicker}</span>
            </div>
            <div className="relative z-10 mt-10">
                <h3 className="font-display text-3xl md:text-4xl text-cream leading-tight">
                    {service.title}
                </h3>
                <p className="mt-3 text-cream/70 max-w-md">{service.desc}</p>
                <div className="mt-6 inline-flex items-center gap-2 label-eyebrow text-yellow">
                    En savoir plus
                    <ArrowUpRight size={14} weight="bold" />
                </div>
            </div>
        </motion.article>
    );
}
